import {
  BorderValue,
  ColorValue,
  ExplicitPropertyTypeValueMap,
  FontValue,
  PropertyTypeValueMap,
} from '../../types/metadata/stylesDefinition';
import { StyleType } from '../../types/metadata/variants';

/*
  This rule system provides a type-safe mapping from style properties (as defined in ExplicitPropertyTypeValueMap/PropertyTypeValueMap)
  to strategies that convert values into CSS-ready outputs.
  Conditional types ensure each property is assigned a rule compatible with its declared value type, and the rule table enforces
  coverage for all explicitly known style properties (those in ExplicitPropertyTypeValueMap).
  Any css property that is not explicitly defined in ExplicitPropertyTypeValueMap
  will be converted using generic value to string strategy.
*/

export type ConversionRule =
  | BooleanRule
  | ColorRule
  | BorderRule
  | FontRule
  | StringRule
  | StringOrNumberRule;

type BooleanRule = { type: 'boolean' };
type ColorRule = { type: 'color' };
type BorderRule = { type: 'border' };
type FontRule = { type: 'font' };
type StringRule = { type: 'string' };
type StringOrNumberRule = { type: 'stringOrNumber'; unit?: string };

type PropertyToRuleMap = {
  [K in StyleType]: Required<
    PropertyTypeValueMap[K]
  > extends Required<FontValue>
    ? FontRule
    : Required<PropertyTypeValueMap[K]> extends Required<ColorValue>
    ? ColorRule
    : Required<PropertyTypeValueMap[K]> extends Required<BorderValue>
    ? BorderRule
    : PropertyTypeValueMap[K] extends boolean
    ? BooleanRule
    : // Handle string | number union types
    [PropertyTypeValueMap[K]] extends [string | number]
    ? [PropertyTypeValueMap[K]] extends [string]
      ? StringRule
      : StringOrNumberRule
    : never;
};

type ExplicitPropertyTypeValueMapKeys = keyof ExplicitPropertyTypeValueMap;

const PROPERTY_TRANSFORMATION_RULES: {
  [K in ExplicitPropertyTypeValueMapKeys]: PropertyToRuleMap[K];
} = {
  [StyleType.width]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.height]: { type: 'stringOrNumber', unit: 'px' },

  [StyleType.margin]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.marginTop]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.marginRight]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.marginBottom]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.marginLeft]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.marginInlineStart]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.marginInlineEnd]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.padding]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.paddingTop]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.paddingRight]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.paddingBottom]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.paddingLeft]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.paddingInlineStart]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.paddingInlineEnd]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.gap]: { type: 'stringOrNumber', unit: 'px' },

  [StyleType.color]: { type: 'color' },
  [StyleType.backgroundColor]: { type: 'color' },
  [StyleType.borderColor]: { type: 'color' },
  [StyleType.borderTopColor]: { type: 'color' },
  [StyleType.borderRightColor]: { type: 'color' },
  [StyleType.borderBottomColor]: { type: 'color' },
  [StyleType.borderLeftColor]: { type: 'color' },
  [StyleType.borderInlineStartColor]: { type: 'color' },
  [StyleType.borderInlineEndColor]: { type: 'color' },
  [StyleType.textDecorationColor]: { type: 'color' },

  [StyleType.font]: { type: 'font' },
  [StyleType.fontFamily]: { type: 'string' },
  [StyleType.fontSize]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.fontWeight]: { type: 'stringOrNumber' },
  [StyleType.fontStyle]: { type: 'string' },
  [StyleType.fontVariant]: { type: 'string' },
  [StyleType.lineHeight]: { type: 'stringOrNumber' },
  [StyleType.letterSpacing]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.textAlign]: { type: 'string' },
  [StyleType.textTransform]: { type: 'string' },

  [StyleType.border]: { type: 'border' },
  [StyleType.borderWidth]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.borderStyle]: { type: 'string' },
  [StyleType.borderRadius]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.borderTopLeftRadius]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.borderTopRightRadius]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.borderBottomLeftRadius]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.borderBottomRightRadius]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.borderStartStartRadius]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.borderStartEndRadius]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.borderEndStartRadius]: { type: 'stringOrNumber', unit: 'px' },
  [StyleType.borderEndEndRadius]: { type: 'stringOrNumber', unit: 'px' },

  [StyleType.booleanValue]: { type: 'boolean' },
  [StyleType.stringEnum]: { type: 'string' },
  [StyleType.numberEnum]: { type: 'stringOrNumber' },
  [StyleType.number]: { type: 'stringOrNumber' },
};

export const getRuleForType = (type: StyleType) => {
  return (
    PROPERTY_TRANSFORMATION_RULES[
      type as keyof typeof PROPERTY_TRANSFORMATION_RULES
    ] || undefined
  );
};
